import {
  createContext,
  useContext as useReactContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { PromiseWithServerResult, Zero } from "@rocicorp/zero";
import { MealPlanningSession } from "@corpuscomplete/core/nutrition/meal-planning-session";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { MealScheduleUI } from "../../meal-schedule";

export namespace MealPlanningSessionList {
  export type Context = {
    userId: string;
    scopeId: string;
  };

  export type MemberOption = {
    userId: string;
    label: string;
  };

  export type CreateError = {
    field: "dateEnd" | "memberUserIds" | "defaultMealScheduleId";
    message: string;
  };

  export type CreateState = {
    dateStart: Temporal.PlainDate;
    dateEnd: Temporal.PlainDate;
    selectedMemberUserIds: readonly string[];
    selectedMealScheduleId: string | null;
    scheduleOptions: readonly { id: string; name: string }[];
    memberOptions: readonly MemberOption[];
    errors: readonly CreateError[];
    canCreate: boolean;
    setDateStart: (date: Temporal.PlainDate) => void;
    setDateEnd: (date: Temporal.PlainDate) => void;
    toggleMember: (userId: string) => void;
    setSelectedMealScheduleId: (id: string) => void;
    reset: () => void;
  };

  export type Mutations = {
    create: (input: Omit<MealPlanningSession.Create, "scopeId">) => PromiseWithServerResult;
  };

  const DataContext = createContext<Context | null>(null);

  export function DataProvider({ children, userId, scopeId }: PropsWithChildren<Context>) {
    return <DataContext value={{ userId, scopeId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error(
        "MealPlanningSessionList.useContext must be used within MealPlanningSessionList.DataProvider",
      );
    }
    return context;
  }

  export function useSessions(): MealPlanningSession.Preview[] {
    const { scopeId } = useContext();
    const [sessions] = useQuery(
      queries.mealPlanningSession.list({ scopeId, limit: 50, cursor: null, dir: "forward" }),
    );
    return sessions;
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { scopeId } = useContext();
    const [, details] = useQuery(
      queries.mealPlanningSession.list({ scopeId, limit: 50, cursor: null, dir: "forward" }),
    );
    return { isLoading: details.type === "unknown" };
  }

  export function useCreateState(): CreateState {
    const { scopeId, userId } = useContext();
    const [spaces] = useQuery(queries.space.byMembership());
    const [schedules] = MealScheduleUI.useByScope(scopeId);
    const today = Temporal.Now.plainDateISO();
    const [dateStart, setDateStart] = useState(today);
    const [dateEnd, setDateEnd] = useState(today.add({ days: 6 }));
    const memberOptions = useMemo<MemberOption[]>(() => {
      const selectedSpace = spaces.find((space) => space.id === scopeId);
      return (
        selectedSpace?.group?.members.map((member) => ({
          userId: member.userId,
          label: member.profile?.name ?? member.userId,
        })) ?? []
      );
    }, [scopeId, spaces]);
    const defaultMemberUserIds = useMemo(
      () => (memberOptions.length > 0 ? memberOptions.map((member) => member.userId) : [userId]),
      [memberOptions, userId],
    );
    const [selectedMemberUserIds, setSelectedMemberUserIds] =
      useState<readonly string[]>(defaultMemberUserIds);
    const [selectedMealScheduleId, setSelectedMealScheduleId] = useState<string | null>(null);

    useEffect(() => {
      setSelectedMealScheduleId((current) => current ?? schedules[0]?.id ?? null);
    }, [schedules]);

    useEffect(() => {
      setSelectedMemberUserIds((current) =>
        sameMembers(current, defaultMemberUserIds) ? current : defaultMemberUserIds,
      );
    }, [defaultMemberUserIds, scopeId]);

    const errors = useMemo<CreateError[]>(() => {
      const next: CreateError[] = [];
      if (Temporal.PlainDate.compare(dateStart, dateEnd) > 0) {
        next.push({ field: "dateEnd", message: "End date must be same as or after start date." });
      }
      if (selectedMemberUserIds.length === 0) {
        next.push({ field: "memberUserIds", message: "Select at least one session member." });
      }
      if (!selectedMealScheduleId) {
        next.push({ field: "defaultMealScheduleId", message: "Select a meal schedule." });
      }
      return next;
    }, [dateEnd, dateStart, selectedMealScheduleId, selectedMemberUserIds.length]);

    return {
      dateStart,
      dateEnd,
      selectedMemberUserIds,
      selectedMealScheduleId,
      scheduleOptions: schedules.map((schedule) => ({ id: schedule.id, name: schedule.name })),
      memberOptions,
      errors,
      canCreate: errors.length === 0,
      setDateStart,
      setDateEnd,
      toggleMember: (memberUserId) => {
        setSelectedMemberUserIds((current) =>
          current.includes(memberUserId)
            ? current.filter((id) => id !== memberUserId)
            : [...current, memberUserId],
        );
      },
      setSelectedMealScheduleId,
      reset: () => {
        const nextToday = Temporal.Now.plainDateISO();
        setDateStart(nextToday);
        setDateEnd(nextToday.add({ days: 6 }));
        setSelectedMemberUserIds(defaultMemberUserIds);
        setSelectedMealScheduleId(schedules[0]?.id ?? null);
      },
    };
  }

  function sameMembers(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((id, index) => id === right[index]);
  }

  export function useMutations(): Mutations {
    const z = useZero();
    const { scopeId } = useContext();
    return useMemo(
      () => ({
        create: (input) => z.mutate(mutators.mealPlanningSession.create({ ...input, scopeId })),
      }),
      [scopeId, z],
    );
  }

  export function preload(zero: Zero, input: { scopeId: string }): void {
    void zero.run(
      queries.mealPlanningSession.list({
        scopeId: input.scopeId,
        limit: 50,
        cursor: null,
        dir: "forward",
      }),
    );
  }
}
